import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  ChevronUp,
  FileX,
  ImageOff,
} from "lucide-react";
import { FaPlus, FaCalendarAlt, FaUser } from "react-icons/fa";
import { MdOutlineTextsms } from "react-icons/md";
import { Card, Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import "./Home.css";
import { FaUserPen } from "react-icons/fa";
import { FaEdit } from 'react-icons/fa';
import { Modal } from "react-bootstrap";
import { CheckCircleFill } from "react-bootstrap-icons";

const CATEGORIES = [
  "Infrastructure",
  "Canteen",
  "Examination",
  "Academics",
  "Fee Payments and Accounts",
   "EduPrime",
  "Hostel Food",
  "Extracurricular and Events",
  "Security",
  "Sports",
  "Housekeeping",
  "Audio-Visual Equipment",
  "Parking",
  "Transport",
  "Library",
  "IT and Networking",
  "Others",
];

const STATUSES = ["Pending", "Ongoing", "Resolved"];

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [userVotes, setUserVotes] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [editWarning, setEditWarning] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const [openReply, setOpenReply] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});

  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;
  const [editImage, setEditImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");

  const showPopup = (message) => {
    setPopupMessage(message);
    setTimeout(() => setPopupMessage(""), 2000);
  };

  const handleReplyChange = (commentId, value) => {
    setReplyTexts((prev) => ({ ...prev, [commentId]: value }));
  };

  const handleReplySubmit = async (comment) => {
    const text = (replyTexts[comment.id] || "").trim();
    if (!text) return showPopup("Reply cannot be empty");

    try {
      const token = localStorage.getItem("authToken");
      const url = `${baseUrl}/user-api/complaints/${expandedCard.complaint_id}/comment/${comment.id}/reply`;
      const res = await axios.post(
        url,
        { text, userEmail: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res?.data?.complaint) {
        setExpandedCard(res.data.complaint);
        setReplyTexts((prev) => ({ ...prev, [comment.id]: "" }));
        setExpandedReplies((prev) => ({ ...prev, [comment.id]: true }));
        setOpenReply(null);
        showPopup("Reply added successfully");
      } else {
        showPopup("Reply added, but failed to refresh comments");
      }
    } catch (err) {
      console.error("Error adding reply:", err);
      showPopup("Failed to add reply");
    }
  };

  const [editComplaint, setEditComplaint] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", category: "" });

  useEffect(() => {
    fetchComplaints();
  }, [categoryFilter, statusFilter]);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const url = `${baseUrl}/user-api/filter-complaints?category=${categoryFilter}&status=${statusFilter}&_t=${Date.now()}`;
      const token = localStorage.getItem("authToken");

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.complaints || [];
      data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setComplaints(data);

      const votes = {};
      data.forEach((complaint) => {
        if (complaint.userVote) {
          votes[complaint.complaint_id] = complaint.userVote;
        }
      });
      setUserVotes(votes);

    } catch (err) {
      console.error("Error fetching complaints:", err);
      if (err.response && err.response.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/complaints-website");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) {
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
   }
   if (days < 365) {
    return `${months} ${months === 1 ? "month" : "months"} ago`;
   }
   return `${years} ${years === 1 ? "year" : "years"} ago`;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1500 * 1024) {
        alert("File too large! Please upload ≤ 1.5MB.");
        return;
      }
      setEditImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeEditImage = () => {
    setEditImage(null);
    setImagePreview(null);
    setEditComplaint((prev) => ({ ...prev, image: null }));
  };

  const submitEditComplaint = async () => {
    try {
      const title = editForm.title.trim();
      const description = editForm.description.trim();

      if (!title || !description) {
        setEditWarning("⚠ Title and description cannot be empty.");
        return;
      }

      const gibberishWordPattern = /\b[b-df-hj-np-tv-z]{4,}\b/gi;
      const hasGibberishWord = (text) => {
        const words = text.split(/\s+/);
        return words.some((word) => gibberishWordPattern.test(word));
      };

      if (hasGibberishWord(title) || hasGibberishWord(description)) {
        setEditWarning("⚠ Please remove random or meaningless words (like 'xiugcunb', 'bcjkhl', etc.) before saving.");
        return;
      }

      if (description.length < 20) {
        setEditWarning("⚠ Description must be at least 20 characters long.");
        return;
      }

      const token = localStorage.getItem("authToken");
      let imageUrl = editComplaint.image;

      if (editImage) {
        const imgData = new FormData();
        imgData.append("file", editImage);
        imgData.append("upload_preset", "complaint_uploads");
        const uploadRes = await axios.post(
          "https://api.cloudinary.com/v1_1/dbsrpikci/image/upload",
          imgData
        );
        imageUrl = uploadRes.data.secure_url;
      }

      const updatedData = { ...editForm, image: imageUrl };

      await axios.put(
        `${baseUrl}/user-api/edit-complaint/${editComplaint.complaint_id}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);

      setComplaints((prev) =>
        prev.map((c) =>
          c.complaint_id === editComplaint.complaint_id
            ? { ...c, ...updatedData }
            : c
        )
      );

      setEditComplaint(null);
      setEditImage(null);
      setImagePreview(null);
      setEditWarning("");
    } catch (err) {
      const backendMsg = err.response?.data?.message;
      if (backendMsg) {
        setEditWarning(backendMsg);
      } else {
        console.error("Error editing complaint:", err);
      }
    }
  };

  const getVisibleAdminComments = (complaint) => {
    if (!complaint.comments) return [];
    return complaint.comments.filter((comment) =>
      comment.text &&
      !comment.text.includes("[Assistant Update]") &&
      !comment.text.toLowerCase().includes("updated status to")
    );
  };

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const toggleDescription = (complaintId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [complaintId]: !prev[complaintId],
    }));
  };

  const handleVote = async (id, type) => {
    const prevVote = userVotes[id];
    try {
      const token = localStorage.getItem("authToken");
      const url = `${baseUrl}/user-api/${type === "upvote" ? "like" : "dislike"}-complaint/${id}`;
      await axios.post(
        url,
        { email: user?.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserVotes((prevVotes) => {
        if (prevVote === type) {
          const updatedVotes = { ...prevVotes };
          delete updatedVotes[id];
          return updatedVotes;
        } else {
          return { ...prevVotes, [id]: type };
        }
      });

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) => {
          if (complaint.complaint_id !== id) return complaint;
          let likes = complaint.likes;
          let dislikes = complaint.dislikes;

          if (prevVote === "upvote") likes = Math.max(0, likes - 1);
          if (prevVote === "downvote") dislikes = Math.max(0, dislikes - 1);

          if (prevVote === type) {
            return { ...complaint, likes, dislikes };
          } else {
            if (type === "upvote") likes += 1;
            else dislikes += 1;
            return { ...complaint, likes, dislikes };
          }
        })
      );

      if (expandedCard && expandedCard.complaint_id === id) {
        let updatedLikes = expandedCard.likes;
        let updatedDislikes = expandedCard.dislikes;

        if (prevVote === "upvote") updatedLikes = Math.max(0, updatedLikes - 1);
        if (prevVote === "downvote") updatedDislikes = Math.max(0, updatedDislikes - 1);

        if (prevVote !== type) {
          if (type === "upvote") updatedLikes += 1;
          else updatedDislikes += 1;
        }

        setExpandedCard({
          ...expandedCard,
          likes: updatedLikes,
          dislikes: updatedDislikes,
        });
      }
    } catch (err) {
      console.error("Error updating vote:", err);
    }
  };

  const handleEditComplaint = (complaint) => {
    setEditComplaint(complaint);
    setEditForm({
      title: complaint.title,
      description: complaint.description,
      category: complaint.category,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteComplaint = async (id) => {
    if (!window.confirm("Are you sure you want to delete this complaint?")) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.delete(`${baseUrl}/user-api/delete-complaint/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setShowDeleteSuccess(true);
        setTimeout(() => setShowDeleteSuccess(false), 3000);

        setComplaints((prev) => prev.filter((c) => c.complaint_id !== id));

        if (expandedCard?.complaint_id === id) {
          setExpandedCard(null);
        }

        if (editComplaint?.complaint_id === id) {
          setEditComplaint(null);
        }
      } else {
        console.error("Failed to delete complaint");
      }
    } catch (err) {
      console.error("Error deleting complaint:", err.response?.data || err.message);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="status-pill status-pending">Pending</span>;
      case "Ongoing":
        return <span className="status-pill status-ongoing">Ongoing</span>;
      case "Resolved":
        return <span className="status-pill status-resolved">Resolved</span>;
      case "Reopened":
        return <span className="status-pill status-reopened">🔄 Reopened</span>;
      default:
        return null;
    }
  };

  const filteredComplaints = complaints.filter((complaint) => {
    const isFlagged = complaint.flagged === true || complaint.flagged?.isFlagged === true;
    if (isFlagged) return false;

    const matchesSearch =
      complaint.title.toLowerCase().includes(search.toLowerCase()) ||
      complaint.description.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = categoryFilter ? complaint.category === categoryFilter : true;
    const matchesStatus = statusFilter ? complaint.status === statusFilter : true;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <Container className="mt-4 home-container mb-2">
      {popupMessage && (
        <div className="home-toast">{popupMessage}</div>
      )}

      <div className="home-page-header">
        <h2>All Support Requests</h2>
        <p>Browse, upvote and track campus issues raised by the community</p>
      </div>

      <div className="modern-filter-container mb-5">
        <div className="modern-dropdown-row">
          <div className="modern-dropdown">
            <input
              type="text"
              placeholder="Search complaints..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="modern-search-input"
            />
          </div>

          <div className="modern-dropdown">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="modern-dropdown">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-wrapper">
          <div className="spinner" />
        </div>
      ) : filteredComplaints.length === 0 ? (
        <div className="empty-state mt-5 mb-5">
          <div className="empty-state-icon-wrap">
            <FileX size={44} className="iconn" />
          </div>
          <h5 className="empty-state-title">No complaints found</h5>
          <p className="empty-state-subtitle">Try changing the filters or submit a new complaint.</p>
          <button className="empty-state-btn" onClick={() => navigate("/complaint-form")}>
            <FaPlus size={13} /> Add Support Request
          </button>
        </div>
      ) : (
        <div className={expandedCard ? "blurred-background" : "cards-grid-enter"}>
          <Row
            className="complaints-grid-row"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1.75rem",
              alignItems: "stretch",
              margin: 0,
              width: "100%",
            }}
          >
            {filteredComplaints.map((complaint, index) => {
              const catLower = (complaint.category || "").toString().toLowerCase();
              const isItCard = catLower.includes("it") && catLower.includes("network");
              const isExpanded = expandedDescriptions[complaint.complaint_id] || false;
              const description = complaint.description || "";
              const shouldTruncate = isItCard ? description.length > 90 : description.length > 200;
              
              return (
                <Col 
                  key={complaint.complaint_id}
                  className="complaints-grid-col"
                  style={{
                    width: "100%",
                    maxWidth: "none",
                    flex: "none",
                    padding: 0,
                    margin: 0,
                    animation: `fadeInUp 0.45s ease-out ${0.05 * index}s both`,
                  }}
                >
                  <Card 
                    className={`card-hover-effect glass-effect rounded-4 custom-card-container w-100 d-flex flex-column ${isItCard ? "it-card-roomy" : ""}`}
                    style={{
                      height: "100%",
                      minHeight: "420px",
                      maxHeight: "600px",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <div className="position-relative complaint-image-wrapper">
                      {complaint.image ? (
                        <img
                          src={complaint.image}
                          alt="Complaint"
                          className="complaint-image"
                          onClick={() => {
                            setModalImageSrc(complaint.image);
                            setShowImageModal(true);
                          }}
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          <ImageOff size={30} strokeWidth={1.6} />
                          <span>No Image Available</span>
                        </div>
                      )}

                      <div
                        className="status-overlay position-absolute"
                        style={{ top: "10px", left: "10px", zIndex: 3 }}
                      >
                        {getStatusBadge(complaint.status)}
                      </div>
                    </div>

                    <div 
                      className="card-body-content" 
                      style={{ 
                        flex: 1, 
                        display: "flex", 
                        flexDirection: "column",
                        overflowY: "auto",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#c4b5fd transparent",
                      }}
                    >
                      <div className="complaint-date-row">
                        <span className="date-icon-wrap">
                          <FaCalendarAlt />
                        </span>
                        {formatDate(complaint.timestamp)}
                      </div>

                      <Card.Title className="card-title-text">
                        {complaint.title}
                      </Card.Title>

                      {/* Description with expand/collapse */}
                      <div style={{ flex: 1 }}>
                        <Card.Text 
                          className={`card-desc-text mb-2 ${isItCard ? "desc-clamp-2" : ""} ${isExpanded ? "expanded" : ""}`}
                        >
                          {isExpanded 
                            ? description 
                            : `${description.substring(0, isItCard ? 90 : 200)}${shouldTruncate ? "..." : ""}`
                          }
                        </Card.Text>

                        {/* Expand/Collapse Button */}
                        {shouldTruncate && (
                          <button
                            className="expand-desc-btn"
                            onClick={() => toggleDescription(complaint.complaint_id)}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? "Show less description" : "Show more description"}
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp size={16} /> Show Less
                              </>
                            ) : (
                              <>
                                <ChevronDown size={16} /> Show More
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      {/* IT details summary */}
                      {(() => {
                        const cat = (complaint.category || "").toString().toLowerCase();
                        const isIt = cat.includes("it") && cat.includes("network");
                        const it = complaint.it_details || {};
                        // const itLocation = it.location;
                        const itConnection = it.connectionType;
                        const itRoom = it.room_number || complaint.room_number;
                        const itSpeed = it.internet_speed || complaint.internet_speed;
                        const itDuration = it.issue_duration || complaint.issue_duration;

                        if (isIt && (itConnection || itRoom || itSpeed || itDuration)) {
                          return (
                            <div className="it-summary mb-2">
                             {itConnection && (
                            <div className="d-flex align-items-center mb-1">
                            <span style={{ marginRight: 8 }}>🔗</span>
                            <small><strong>Connection:</strong>&nbsp;{itConnection}</small>
                            </div>
                            )}
                               
                              {itRoom && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>🏷️</span><small><strong>Room:</strong>&nbsp;{itRoom}</small></div>}
                              {itSpeed && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>📶</span><small><strong>Speed:</strong>&nbsp;{itSpeed}</small></div>}
                              {itDuration && <div className="d-flex align-items-center mb-1"><span style={{ marginRight: 8 }}>⏱️</span><small><strong>Duration:</strong>&nbsp;{itDuration}</small></div>}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {getVisibleAdminComments(complaint).length > 0 && (
                        <button
                          className="admin-update-btn"
                          onClick={() => setExpandedCard(complaint)}
                        >
                          Show Admin Updates <ChevronDown size={16} />
                        </button>
                      )}

                      {/* Footer: category + edit/delete + votes */}
                      <div className="mt-auto d-flex w-100 align-items-center pt-2 px-0 card-footer-row">
                        <span className="category-tag">
                          {complaint.category}
                        </span>

                        <div className="d-flex align-items-center gap-2 ms-auto">
                          <button
                            className={`vote-btn vote-btn-up ${userVotes[complaint.complaint_id] === "upvote" ? "active" : ""}`}
                            onClick={() => handleVote(complaint.complaint_id, "upvote")}
                          >
                            <ThumbsUp size={18} />
                            {complaint.likes}
                          </button>

                          <button
                            className={`vote-btn vote-btn-down ${userVotes[complaint.complaint_id] === "downvote" ? "active" : ""}`}
                            onClick={() => handleVote(complaint.complaint_id, "downvote")}
                          >
                            <ThumbsDown size={18} />
                            {complaint.dislikes}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* Floating expanded card for Admin Updates */}
      {expandedCard && (
        <div className="overlay">
          <Card
            className="popup-card rounded-4 card-background-gradient p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-btn-inside-modalh"
              onClick={() => setExpandedCard(null)}
            >
              ✕
            </button>

            <div className="mb-2">{getStatusBadge(expandedCard.status)}</div>

            <Card.Text className="popup-date-row">
              <span className="date-icon-wrap popup-date-icon">
                <FaCalendarAlt style={{ fontSize: "14px" }} />
              </span>
              {formatDate(expandedCard.timestamp)}
            </Card.Text>

            <Card.Title className="fw-bold text-dark mt-3 fs-4">
              {expandedCard.title}
            </Card.Title>

            {(() => {
              const cat = (expandedCard.category || "").toString().toLowerCase();
              const isIt = cat.includes("it") && cat.includes("network");
              const itRoom = expandedCard.it_details?.room_number || expandedCard.room_number;
              const itSpeed = expandedCard.it_details?.internet_speed || expandedCard.internet_speed;
              const itDuration = expandedCard.it_details?.issue_duration || expandedCard.issue_duration;
              // const itMobile = expandedCard.it_details?.mobile_number || expandedCard.mobile_number;

              if (isIt && (itRoom || itSpeed || itDuration)) {
                return (
                  <div className="it-details-inline mb-3">
                    {itRoom && (
                      <div className="d-flex align-items-center mb-1">
                        <span className="me-2">🏷️</span>
                        <small><strong>Room:</strong> {itRoom}</small>
                      </div>
                    )}
                    {itSpeed && (
                      <div className="d-flex align-items-center mb-1">
                        <span className="me-2">📶</span>
                        <small><strong>Internet Speed:</strong> {itSpeed}</small>
                      </div>
                    )}
                    {itDuration && (
                      <div className="d-flex align-items-center mb-1">
                        <span className="me-2">⏱️</span>
                        <small><strong>Duration:</strong> {itDuration}</small>
                      </div>
                    )}
                    {/* {itMobile && (
                      <div className="d-flex align-items-center mb-1">
                        <span className="me-2">📞</span>
                        <small><strong>Mobile:</strong> {itMobile}</small>
                      </div>
                    )} */}
                  </div>
                );
              }
              return null;
            })()}

            <Card.Text className="text-dark mb-3">{expandedCard.description}</Card.Text>

            <div className="admin-updates mb-3">
              <h5 className="mb-2">
                <MdOutlineTextsms className="me-2" /> Admin Updates:
              </h5>

              {expandedCard.comments &&
                expandedCard.comments.filter((comment) =>
                  comment.text &&
                  !comment.text.includes("[Assistant Update]") &&
                  !comment.text.toLowerCase().includes("updated status to")
                ).length > 0 ? (
                expandedCard.comments
                  .filter((comment) =>
                    comment.text &&
                    !comment.text.includes("[Assistant Update]") &&
                    !comment.text.toLowerCase().includes("updated status to")
                  )
                  .map((comment) => {
                    const isStudent = comment.role === "student";
                    const displayName = isStudent ? "Student" : (comment.email || "Admin");
                    const borderColor = isStudent ? "#ff6b6b" : "purple";
                    return (
                      <div
                        key={comment.id}
                        className="update-entry mb-2 p-3"
                        style={{
                          borderLeft: `4px solid ${borderColor}`,
                        }}
                      >
                        <div className="d-flex align-items-center mb-1">
                          <FaUser className="me-2" size={18} style={{ color: borderColor }} />
                          <strong style={{ color: borderColor }}>{displayName}</strong>
                        </div>
                        <div style={{ marginLeft: "1.8rem" }}>{comment.text}</div>

                        {comment.replies && comment.replies.length > 0 && (
                          <div style={{ marginLeft: "2.4rem", marginTop: "0.6rem" }}>
                            {(expandedReplies[comment.id]
                              ? comment.replies
                              : comment.replies.slice(0, 3)
                            ).map((r) => (
                              <div
                                key={r.id}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <span
                                  style={{
                                    color: "#ff6b6b",
                                    fontSize: "1.3rem",
                                    marginTop: "14px",
                                  }}
                                >
                                  ↳
                                </span>

                                <div className="reply-card" style={{ flex: 1 }}>
                                  <strong
                                    style={{
                                      color: "#ff6b6b",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    Student
                                  </strong>

                                  <div style={{ fontSize: "0.95rem" }}>{r.text}</div>
                                </div>
                              </div>
                            ))}

                            {comment.replies.length > 5 && (
                              <div style={{ marginLeft: "2.5rem" }}>
                                <button
                                  className="btn btn-link p-0"
                                  style={{
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                  }}
                                  onClick={() => toggleReplies(comment.id)}
                                >
                                  {expandedReplies[comment.id]
                                    ? "View less replies"
                                    : `View more replies (${comment.replies.length - 3})`}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {comment.role === "admin" && user?.email === expandedCard.user_id && (
                          <div style={{ marginLeft: "1.8rem", marginTop: "0.5rem" }}>
                            {openReply === comment.id ? (
                              <div className="d-flex gap-2">
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={replyTexts[comment.id] || ""}
                                  onChange={(e) => handleReplyChange(comment.id, e.target.value)}
                                  placeholder="Write a reply..."
                                ></textarea>
                                <div className="d-flex flex-column gap-1 ms-2">
                                  <button className="btn btn-primary btn-sm" onClick={() => handleReplySubmit(comment)}>Send</button>
                                  <button className="btn btn-secondary btn-sm" onClick={() => { setOpenReply(null); setReplyTexts((prev) => ({ ...prev, [comment.id]: "" })); }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <button className="btn btn-link btn-sm" onClick={() => setOpenReply(comment.id)}>Reply</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
              ) : (
                <p className="text-muted" style={{ marginLeft: "1.8rem" }}>
                  No admin updates yet.
                </p>
              )}
            </div>

            <div className="mt-auto d-flex w-100 align-items-center pt-2 px-0 card-footer-row">
              <span className="category-tag">
                {expandedCard.category}
              </span>

              <div className="d-flex align-items-center gap-2 ms-auto">
                <button
                  className={`vote-btn vote-btn-up ${userVotes[expandedCard.complaint_id] === "upvote" ? "active" : ""}`}
                  onClick={() => handleVote(expandedCard.complaint_id, "upvote")}
                >
                  <ThumbsUp size={18} />
                  {expandedCard.likes}
                </button>

                <button
                  className={`vote-btn vote-btn-down ${userVotes[expandedCard.complaint_id] === "downvote" ? "active" : ""}`}
                  onClick={() => handleVote(expandedCard.complaint_id, "downvote")}
                >
                  <ThumbsDown size={18} />
                  {expandedCard.dislikes}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        size="lg"
        contentClassName="bg-dark border-0"
        style={{ zIndex: 9999 }}
        backdropClassName="custom-image-backdrop"
      >
        <Modal.Body
          className="p-0 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.95)" }}
        >
          <img
            src={modalImageSrc}
            alt="Full preview"
            style={{
              width: "auto",
              maxWidth: "80%",
              height: "auto",
              maxHeight: "65vh",
              borderRadius: "10px",
              objectFit: "contain",
              boxShadow: "0 0 15px rgba(0,0,0,0.5)",
            }}
          />
          <button
            onClick={() => setShowImageModal(false)}
            style={{
              position: "absolute",
              top: "15px",
              right: "20px",
              background: "rgba(255,255,255,0.2)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              fontSize: "1.5rem",
              cursor: "pointer",
              zIndex: 10000,
            }}
          >
            ×
          </button>
        </Modal.Body>
      </Modal>

      <button
        className="add-complaint-btn"
        onClick={() => navigate("/complaint-form")}
      >
        <FaPlus className="plus-icon" /> Register Complaint
      </button>

      <div
        className="feedback-button"
        onClick={() =>
          window.open(
            "https://docs.google.com/forms/d/e/1FAIpQLSdpEmSGlS8hwIR58A3zW9vm9kTVBuhiONbA6zDjAixeMfELnw/viewform?usp=header",
            "_blank"
          )
        }
      >
        <MdOutlineTextsms size={20} />
        <span>Feedback</span>
      </div>

      <div style={{ height: "40px" }}></div>
    </Container>
  );
};

export default Home;